import { getDeviceLocale, reportError } from "@cabocheck/shared";
import type { LoaderFunctionArgs } from "react-router";
import { redirect } from "react-router";
import { Outlet, useLoaderData, useLocation, useParams } from "react-router";
import invariant from "invariant";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { client } from "~/client/client";
import { NavBar } from "~/features/public/NavBar";
import { parseOpeningTimes } from "~/features/microsite/OpenningTimes";
import { smallerSortFirst } from "~/utils/apis/getMedicalTestProfilesList";
import { loadFAQs } from "~/utils/apis/loadFAQs";
import { getBaseParams } from "~/utils/getBaseParams";
import { getContrastColor } from "./getContrastColor";

export async function loader({ request, params }: LoaderFunctionArgs) {
  const { slug } = params;
  invariant(slug, "slug is required");
  const { data: responseData, error } = await client.v1.getApiPartnerLandingByCode({
    ...(await getBaseParams(request, { withDeviceGuid: false })),
    code: slug,
  });

  if (error) reportError("Partner request failed", error);

  if (!responseData?.response) {
    return redirect("/");
  }

  const appointmentTypes = await client.v1.getApiPartnerAppointmentTypeList(responseData?.response?.partner_guid, {
    ...(await getBaseParams(request, { withDeviceGuid: false })),
  });

  const device_locale = getDeviceLocale(request);
  const faqs = loadFAQs(device_locale).then((r) => r.filter((faq) => faq.available_for_microsite));

  const parsedOpeningTimes = parseOpeningTimes(responseData.response.opening_time);

  return {
    parsedOpeningTimes,
    ...responseData.response,
    medical_test_profiles: responseData.response.medical_test_profiles?.sort(smallerSortFirst),
    faqs: await faqs,
    appointmentTypes: appointmentTypes.data?.response?.partner_appointment_types,
  };
}

export default function PharmacyHome() {
  const { t } = useTranslation();
  const data = useLoaderData<typeof loader>();
  const params = useParams();
  const slug = params.slug;

  const location = useLocation();
  const path = location.pathname.split("#")[0];

  const location_id = location.hash ? location.hash.match(/#([^?]+)/)?.[1] : "start";

  const [activeSection, setActiveSection] = useState(location_id);
  useEffect(() => {
    const handleScroll = () => {
      const sections = ["start", "termin", "vorteile", "angebot", "ablauf", "faq"];
      const currentSection = sections.find((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 150 && rect.bottom >= 150;
        }
        return false;
      });
      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [path]);

  const primaryColorContrast = data.primary_color ? getContrastColor(data.primary_color) : "#000000";
  const secondaryColorContrast = data.secondary_color ? getContrastColor(data.secondary_color) : "#000000";

  return (
    <div
      className="relative font-open-sans antialiased"
      style={{
        // @ts-expect-error noe
        "--primary-color": data?.primary_color,
        "--secondary-color": data?.secondary_color,
        "--primary-color-200": data?.primary_color ? data.primary_color + "30" : "#dbeafe",
        "--secondary-color-200": data?.secondary_color ? data.secondary_color + "30" : "#fce7f3",
        "--primary-color-contrast": primaryColorContrast,
        "--secondary-color-contrast": secondaryColorContrast,
      }}
    >
      <NavBar
        activeSection={activeSection}
        title={data.hide_pharmacy_name ? undefined : data.partner_name}
        logo={data.logo_url}
        logoTo={`/p/${slug}`}
        navItems={[
          { to: `/p/${slug}#start`, label: t("Start"), id: "start" },
          ...(data.appointmentTypes?.length || data.iframe
            ? [{ to: `/p/${slug}#termin`, label: t("Termin"), id: "termin" }]
            : []),
          { to: `/p/${slug}#vorteile`, label: t("Vorteile"), id: "vorteile" },
          { to: `/p/${slug}/products`, label: t("Angebot"), id: "angebot" },
          { to: `/p/${slug}#ablauf`, label: t("Ablauf"), id: "ablauf" },
          { to: `/p/${slug}#faq`, label: t("FAQ"), id: "faq" },
        ]}
        sticky
      />
      <Outlet />
    </div>
  );
}
